export type SearchWorkspaceAgentId = "accident-search" | "legal-search";

export type SearchWorkspaceBadgeField = {
  key: string;
  label: string;
  options: string[];
};

export type SearchWorkspaceTextField = {
  key: string;
  label: string;
  placeholder: string;
};

export type SearchWorkspaceConfig = {
  agentId: SearchWorkspaceAgentId;
  title: string;
  heroSubtitle: string;
  composerPlaceholder: string;
  artifactTag: "cases" | "laws";
  itemTag: "case" | "law";
  resultNoun: string;
  searchStageTitle: string;
  reportStageTitle: string;
  generatingLabel: string;
  historyLabel: string;
  emptyHistoryLabel: string;
  badgeFields: SearchWorkspaceBadgeField[];
  advancedFields: SearchWorkspaceTextField[];
  toolLabels: Record<string, string>;
  buildPrompt: (fields: Record<string, string>, description: string) => string | null;
  artifactTitle: (xmlContent: string) => string;
};

const unescapeXml = (value: string) =>
  value.replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">");

// ChatInterface.extractArtifactTitle의 cases 분기와 같은 규칙 — 아티팩트
// 라이브러리와 홈 채팅에서 보이는 제목과 어긋나지 않게 유지한다.
const casesArtifactTitle = (xmlContent: string) => {
  const caseCount = xmlContent.match(/<case\b/g)?.length ?? 0;
  const firstTitle = xmlContent.match(/<case[^>]*>[\s\S]*?<title>([^<]+)<\/title>/)?.[1]?.trim();
  if (firstTitle) {
    const title = unescapeXml(firstTitle.length > 25 ? `${firstTitle.substring(0, 22)}...` : firstTitle);
    return caseCount > 1 ? `${title} 외 ${caseCount - 1}건` : title;
  }
  const cause = xmlContent.match(/<accidentCause>([^<]+)<\/accidentCause>/)?.[1]?.trim();
  if (cause) return caseCount > 1 ? `${unescapeXml(cause)} 사고사례 ${caseCount}건` : `${unescapeXml(cause)} 사고사례`;
  return "사고사례 데이터";
};

const lawsArtifactTitle = (xmlContent: string) => {
  const lawCount = xmlContent.match(/<law\b/g)?.length ?? 0;
  const firstAct = xmlContent.match(/<act>([^<]+)<\/act>/)?.[1]?.trim();
  const firstArticle = xmlContent.match(/<article>([^<]+)<\/article>/)?.[1]?.trim();
  if (firstAct && firstArticle) {
    const title = unescapeXml(`${firstAct} ${firstArticle}`);
    return lawCount > 1 ? `${title} 외 ${lawCount - 1}건` : title;
  }
  if (firstAct) return lawCount > 1 ? `${unescapeXml(firstAct)} 관련 법령 ${lawCount}건` : unescapeXml(firstAct);
  return `관련 법령 ${lawCount || ""}조문`;
};

export const SEARCH_WORKSPACE_CONFIGS: Record<SearchWorkspaceAgentId, SearchWorkspaceConfig> = {
  "accident-search": {
    agentId: "accident-search",
    title: "사고 사례 검색",
    heroSubtitle: "작업 상황을 설명하거나 조건을 선택하면 실제 중대재해 사례에서 유사 사고를 찾아 정리해드립니다.",
    composerPlaceholder: "작업 상황이나 찾고 싶은 사고를 설명해주세요. 예: 아파트 신축 현장에서 타워크레인 설치 작업 중",
    artifactTag: "cases",
    itemTag: "case",
    resultNoun: "사고사례",
    searchStageTitle: "유사 사고사례 검색",
    reportStageTitle: "검색 결과 정리",
    generatingLabel: "사고사례 데이터를 검색하고 있습니다",
    historyLabel: "사고 사례 검색 기록",
    emptyHistoryLabel: "아직 사고 사례 검색 기록이 없습니다.",
    badgeFields: [
      { key: "workTypeMain", label: "공사 종류", options: ["건축", "토목", "전기", "기계", "화학", "기타"] },
      { key: "accidentCause", label: "사고 원인", options: ["추락", "끼임", "충돌", "감전", "화재", "붕괴", "질식", "폭발", "기타"] },
      { key: "publicPrivateType", label: "공공/민간", options: ["공공", "민간"] },
    ],
    advancedFields: [
      { key: "region", label: "지역", placeholder: "예: 서울, 인천" },
      { key: "accidentName", label: "사고명 키워드", placeholder: "예: 비계 붕괴" },
    ],
    toolLabels: {
      "accident-search": "사고사례 데이터 검색",
      accidentSearchTool: "사고사례 데이터 검색",
      "platform-context-read": "플랫폼 컨텍스트 확인",
      platformContextReadTool: "플랫폼 컨텍스트 확인",
    },
    // Classic 폼과 같은 문장 형태를 유지해 백엔드 에이전트가 받는 프롬프트가
    // 리뉴얼 전과 달라지지 않게 한다.
    buildPrompt: (fields, description) => {
      const parts: string[] = [];
      if (fields.workTypeMain) parts.push(`${fields.workTypeMain} 공사`);
      if (fields.accidentCause) parts.push(`${fields.accidentCause} 사고`);
      if (fields.region) parts.push(`${fields.region} 지역`);
      if (fields.publicPrivateType) parts.push(`${fields.publicPrivateType} 현장`);
      if (fields.accidentName) parts.push(`"${fields.accidentName}" 관련`);
      const trimmedDescription = description.trim();
      if (parts.length === 0 && !trimmedDescription) return null;
      const condition = parts.length > 0 ? `${parts.join(", ")}에 해당하는 유사 사고 사례를 검색해줘.` : "";
      if (!trimmedDescription) return condition;
      if (!condition) return `${trimmedDescription}\n\n위 상황과 관련된 유사 사고 사례를 검색해줘.`;
      return `${trimmedDescription}\n\n${condition}`;
    },
    artifactTitle: casesArtifactTitle,
  },
  "legal-search": {
    agentId: "legal-search",
    title: "법령 검색",
    heroSubtitle: "궁금한 안전 조치나 상황을 입력하면 관련 법령 조문을 원문과 함께 찾아 정리해드립니다.",
    composerPlaceholder: "찾고 싶은 법령 키워드나 상황을 입력해주세요. 예: 추락 방지, 안전난간 설치 기준",
    artifactTag: "laws",
    itemTag: "law",
    resultNoun: "법령",
    searchStageTitle: "관련 법령 조문 검색",
    reportStageTitle: "검색 결과 정리",
    generatingLabel: "관련 법령 조문을 검색하고 있습니다",
    historyLabel: "법령 검색 기록",
    emptyHistoryLabel: "아직 법령 검색 기록이 없습니다.",
    badgeFields: [
      { key: "category", label: "카테고리", options: ["위험성평가", "안전관리계획서", "중대재해", "건설산업", "시설물안전", "건축안전", "소방안전"] },
      { key: "lawType", label: "법령 구분", options: ["법률", "대통령령", "고용노동부령", "국토교통부령"] },
    ],
    advancedFields: [
      { key: "actName", label: "법령명", placeholder: "예: 산업안전보건법" },
    ],
    toolLabels: {
      "law-search": "법령 조문 검색",
      lawSearchTool: "법령 조문 검색",
      "law-overview": "법령 개요 조회",
      lawOverviewTool: "법령 개요 조회",
      "law-structure": "법령 구조 탐색",
      lawStructureTool: "법령 구조 탐색",
      "law-article-detail": "조문 상세 확인",
      lawArticleDetailTool: "조문 상세 확인",
      "law-files": "별표·서식 조회",
      lawFilesTool: "별표·서식 조회",
      "platform-context-read": "플랫폼 컨텍스트 확인",
      platformContextReadTool: "플랫폼 컨텍스트 확인",
    },
    buildPrompt: (fields, description) => {
      const parts: string[] = [];
      const trimmedDescription = description.trim();
      if (trimmedDescription) parts.push(`"${trimmedDescription}"`);
      if (fields.actName) parts.push(fields.actName);
      if (fields.category) parts.push(`(${fields.category} 분야)`);
      if (fields.lawType) parts.push(`${fields.lawType} 기준`);
      if (parts.length === 0) return null;
      return `${parts.join(" ")} 관련 법령 조문을 검색해줘.`;
    },
    artifactTitle: lawsArtifactTitle,
  },
};

export const isSearchWorkspaceAgent = (agentId: string): agentId is SearchWorkspaceAgentId =>
  agentId === "accident-search" || agentId === "legal-search";

// riskAssessmentAssistantText와 같은 역할 — 아티팩트/도구 XML을 걷어내고
// 사용자에게 보여줄 마크다운 요약만 남긴다.
export const extractSearchWorkspaceAssistantText = (content: string) => content
  .replace(/<think>[\s\S]*?(?:<\/think>|$)/gi, "")
  .replace(/<action\b[^>]*>[\s\S]*?(?:<\/action>|$)/gi, "")
  .replace(/<eval_feedback>[\s\S]*?(?:<\/eval_feedback>|$)/gi, "")
  .replace(/<query_used>[\s\S]*?(?:<\/query_used>|$)/gi, "")
  .replace(/<ask_user>[\s\S]*?(?:<\/ask_user>|$)/gi, "")
  .replace(/<artifact\b[^>]*>/gi, "")
  .replace(/<\/artifact>/gi, "")
  .replace(/<cases(?:\s+[^>]*)?>[\s\S]*?(?:<\/cases>|$)/gi, "")
  .replace(/<laws(?:\s+[^>]*)?>[\s\S]*?(?:<\/laws>|$)/gi, "")
  .replace(/```xml\s*<body>\s*/gi, "")
  .replace(/```\s*$/g, "")
  .replace(/<\/?body>/gi, "")
  .trim();
